import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const products = [
  {
    name: "NATURELO One Daily Multivitamin for Women",
    description: "A whole-food based multivitamin designed to support daily energy, hair, skin, and nail health as part of a balanced wellness routine.",
    bestFor: "Daily Wellness",
    benefits: ["Whole-food based", "Supports energy levels", "Hair, skin & nail support"],
    imageUrl: "https://naturelo.com/cdn/shop/files/NAT-1D-WOMEN-120-2401_Front_wCaps_1100x.jpg?v=1756920249",
    affiliateLink: "https://amzn.to/4qnOf5K"
  },
  {
    name: "Reusable Water Bottle",
    description: "Stay hydrated throughout the day with a quality water bottle you'll actually use.",
    bestFor: "Daily Hydration",
    benefits: ["Encourages hydration", "Eco-friendly", "Temperature retention"]
  },
  {
    name: "Blue Light Blocking Glasses",
    description: "Reduce eye strain from screens, especially in the evening hours.",
    bestFor: "Screen Time",
    benefits: ["Reduces eye strain", "May improve sleep", "Stylish options available"]
  },
  {
    name: "Posture Corrector",
    description: "Gentle reminder to maintain good posture, especially during desk work.",
    bestFor: "Desk Workers",
    benefits: ["Encourages good posture", "Comfortable wear", "Adjustable fit"]
  },
  {
    name: "Pill Organizer",
    description: "Keep your supplements and medications organized for the week.",
    bestFor: "Supplement Routine",
    benefits: ["Stay organized", "Never miss a dose", "Travel-friendly options"]
  },
  {
    name: "Foam Roller",
    description: "For self-massage and muscle recovery after workouts or long days.",
    bestFor: "Muscle Recovery",
    benefits: ["Relieves tension", "Improves mobility", "Affordable recovery tool"]
  },
  {
    name: "Resistance Bands Set",
    description: "Versatile exercise tools for strength training at home or on the go.",
    bestFor: "Home Fitness",
    benefits: ["Portable workout", "Various resistance levels", "Full-body training"]
  },
  {
    name: "Sunrise Alarm Clock",
    description: "Gradual light that mimics sunrise for a gentler, more natural wake-up.",
    bestFor: "Better Mornings",
    benefits: ["Gentle wake-up", "May improve energy", "Simulates natural light"]
  },
  {
    name: "Tongue Scraper",
    description: "A simple oral hygiene tool used in Ayurvedic practice for fresh breath.",
    bestFor: "Oral Hygiene",
    benefits: ["Fresher breath", "Simple to use", "Stainless steel lasts forever"]
  }
];

const DailyHealthPicks = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="dailyhealth-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/wellness" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Wellness
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Wellness
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Daily Health Picks
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            Practical items for everyday wellbeing. These simple tools and products support healthy habits that compound over time.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Small Changes, Big Impact
          </h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            You don't need to overhaul your entire life to feel better. Often, it's the small, consistent habits that make the biggest difference over time.
          </p>
          <p className="text-[#374151] leading-relaxed">
            These products support simple daily practices—staying hydrated, moving your body, sleeping better, and taking care of the basics.
          </p>
        </div>
      </section>

      {/* Products */}
      <section data-testid="dailyhealth-products" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Daily Health Essentials
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {products.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Choose
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Focus on fundamentals:</strong> Hydration, movement, and sleep are the foundation of health.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Choose what you'll use:</strong> The best product is one that fits naturally into your routine.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Start with one:</strong> Add one new habit at a time rather than trying to change everything at once.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Visibility helps:</strong> Keep health tools visible—you're more likely to use what you see.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice variant="inline" />
        </div>
      </section>
    </Layout>
  );
};

export default DailyHealthPicks;
